/**
 * Service for handling user idle timeout
 * This service tracks user activity and logs out the user after a specified period of inactivity
 */

import { logout } from './authService';

// Default timeout duration in milliseconds (30 minutes)
const DEFAULT_TIMEOUT_DURATION = 30 * 60 * 1000;

// Events to track for user activity
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keypress',
  'scroll',
  'touchstart',
  'click'
];

let timeoutId = null;
let timeoutDuration = DEFAULT_TIMEOUT_DURATION;
let isInitialized = false;
let warningCallback = null;
let warningTimeoutId = null;
let warningTime = 60 * 1000; // 1 minute before logout

/**
 * Reset the idle timeout
 * @private
 */
const resetTimeout = () => {
  if (timeoutId) {
    clearTimeout(timeoutId);
  }
  
  if (warningTimeoutId) {
    clearTimeout(warningTimeoutId);
  }
  
  // Set warning timeout (1 minute before logout)
  warningTimeoutId = setTimeout(() => {
    if (warningCallback) {
      warningCallback();
    }
  }, timeoutDuration - warningTime);
  
  // Set logout timeout
  timeoutId = setTimeout(() => {
    console.log('User has been idle for too long, logging out...');
    
    // Get current path to redirect back after login
    const currentPath = window.location.pathname;
    
    // Perform logout
    logout();
    
    // Redirect to login with auto_logout flag and redirect path
    window.location.href = `/login?auto_logout=true&redirect=${encodeURIComponent(currentPath)}`;
  }, timeoutDuration);
};

/**
 * Activity event handler
 * @private
 */
const handleActivity = () => {
  resetTimeout();
};

/**
 * Initialize the idle timeout service
 * @param {Object} options - Configuration options
 * @param {number} options.timeoutDuration - Timeout duration in milliseconds (default: 5 minutes)
 * @param {number} options.warningTime - Warning time before logout in milliseconds (default: 1 minute)
 * @param {Function} options.onWarning - Callback function to be called when warning time is reached
 */
export const initIdleTimeout = (options = {}) => {
  // If already initialized, clean up first
  if (isInitialized) {
    cleanupIdleTimeout();
  }
  
  // Set timeout duration
  timeoutDuration = options.timeoutDuration || DEFAULT_TIMEOUT_DURATION;
  
  // Set warning time
  if (options.warningTime !== undefined) {
    warningTime = options.warningTime;
  }
  
  // Set warning callback
  warningCallback = options.onWarning;
  
  // Add event listeners for user activity
  ACTIVITY_EVENTS.forEach(eventType => {
    window.addEventListener(eventType, handleActivity);
  });
  
  // Start the initial timeout
  resetTimeout();
  
  isInitialized = true;
  console.log(`Idle timeout initialized with duration: ${timeoutDuration / 1000} seconds`);
};

/**
 * Clean up the idle timeout service
 */
export const cleanupIdleTimeout = () => {
  if (!isInitialized) {
    return;
  }
  
  // Clear timeouts
  if (timeoutId) {
    clearTimeout(timeoutId);
    timeoutId = null;
  }
  
  if (warningTimeoutId) {
    clearTimeout(warningTimeoutId);
    warningTimeoutId = null;
  }
  
  // Remove event listeners
  ACTIVITY_EVENTS.forEach(eventType => {
    window.removeEventListener(eventType, handleActivity);
  });
  
  isInitialized = false;
  console.log('Idle timeout cleaned up');
};

/**
 * Reset the idle timeout manually
 */
export const resetIdleTimeout = () => {
  if (isInitialized) {
    resetTimeout();
  }
};

/**
 * Check if the idle timeout service is initialized
 * @returns {boolean} True if initialized, false otherwise
 */
export const isIdleTimeoutInitialized = () => {
  return isInitialized;
};

/**
 * Get the current timeout duration
 * @returns {number} Timeout duration in milliseconds
 */
export const getTimeoutDuration = () => {
  return timeoutDuration;
};

/**
 * Set a new timeout duration
 * @param {number} duration - New timeout duration in milliseconds
 */
export const setTimeoutDuration = (duration) => {
  timeoutDuration = duration;
  if (isInitialized) {
    resetTimeout();
  }
};
